import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Row, Space } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { RiArrowLeftLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import isObject from 'lodash/isObject';
import urlPagePushNotifications from '../../../../../../urls/urlPagePushNotifications';
import urlPagePushNotificationDetails from '../../../../../../urls/urlPagePushNotificationDetails';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import BreadCrumbs from '../../../../../../layout/components/breadcrumbs';
import useModifiedDynamicDataErrors from '../../../../../../app/hooks/useModifiedDynamicDataErrors';
import { fieldTypeToValidationRule, TYPE_IMAGE, TYPE_OBJECT } from '../../../../../Template builder/const/templates';
import isFieldRequired from '../../../../../Content/utils/isFieldRequired';
import Yup from '../../../../../../vendor/yup';
import { mkValidationSchema } from '../../../../forms/FormCreateLocalNotification/FormCreateLocalNotification.const';
import createFileFromURL from '../../../../../../lib/createFileFromURL';
import handleResponse from '../../../../../../lib/handleResponse';
import transformErrors from '../../../../../../lib/transformErrors';
import FormEditPushNotification from '../../../../forms/FormEditPushNotification';
import {
  useGetSpecificPushNotificationQuery,
  useUpdatePushNotificationMutation,
} from '../../../../api/pushNotificationsApiSlice';



let validation = {};

const PageEditPushNotification = () => {
  const intl = useIntl();
  const language = useSelector(({ customise: { language } }) => language);
  const { notificationId } = useParams();

  const navigate = useNavigate();
  const [ initValues, setInitValues ] = useState({ data: {} });
  const [ formErrors, setFormErrors ] = useState({});

  const { data: pushNotification = {}, isFetching } = useGetSpecificPushNotificationQuery({ notificationId, includes: '?include=dynamic_data,template' });
  const [ updatePushNotification, { isLoading: isUpdatingPushNotification } ] = useUpdatePushNotificationMutation();

  const modifiedErrors = useModifiedDynamicDataErrors(
    pushNotification?.template?.data.id,
    pushNotification?.template?.data,
    formErrors,
    language,
  );

  const getDynamicData = (initialValues, detailsData) => {
    const data = {};
    const dataSchema = {};

    detailsData?.template?.data?.fields?.data.filter((field) => field.type !== TYPE_OBJECT)
      .forEach(async (field) => {
        const pushNotificationField = detailsData?.dynamic_data?.data?.find((item) => item.template_field_id === field.id);

        if (isFieldRequired(field?.validations?.data || [])) {
          dataSchema[field.full_name] = Yup.object().shape({
            template_field_id: Yup.number().required(),
            ...fieldTypeToValidationRule[field.type],
          });
        }

        if (field.type === TYPE_IMAGE) {
          if (pushNotificationField?.value) {
            const fileName = pushNotificationField.value?.split('/').pop();
            const file = await createFileFromURL(pushNotificationField?.value, fileName);

            data[field.full_name] = {
              value: [ {
                uid: '-1',
                name: pushNotificationField?.value,
                thumbUrl: pushNotificationField?.value,
                status: 'done',
                originFileObj: file,
                url: pushNotificationField?.value,
              } ],
              template_field_id: field.id,
            };
          } else {
            data[field.full_name] = {
              value: [],
              template_field_id: field.id,
            };
          }
        } else {
          data[field.full_name] = {
            value: pushNotificationField?.value,
            template_field_id: field.id,
          };
        }

        setInitValues((prevState) => ({ ...prevState, data }));
      });

    validation = mkValidationSchema({
      data: Yup.object().shape(dataSchema),
    });

    return data;
  };

  useEffect(() => {
    const initialValues = {
      title: pushNotification?.title,
      template_id: pushNotification?.template?.data?.id,
      data: {},
    };

    setInitValues(initialValues);
    getDynamicData(initialValues, pushNotification);
  }, [ isFetching ]);


  const onSubmit = (data) => {
    updatePushNotification({ id: notificationId, values: data })
      .unwrap()
      .then(() => handleResponse(
        'success',
        intl,
        'ui-general-updated-successfully',
        [ () => navigate(urlPagePushNotifications()) ],
      ))
      .catch((error) => handleResponse(
        'error',
        intl,
        'ui-general-update-failed',
        [ () => {
          if (isObject(error?.data?.errors)) {
            setFormErrors(transformErrors(error?.data?.errors));
          }
        } ],
        error,
      ));
  };

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPagePushNotifications()}>
              <IntlMessages id='push-notifications-breadcrumbs' />
            </Link>
          }
          breadCrumbParent2={
            <Link to={urlPagePushNotificationDetails({ notificationId })}>
              {notificationId}
            </Link>
          }
          breadCrumbActive={<IntlMessages id='ui-general-edit' />}
        />

        <Col md={12} span={24}>
          <Row
            gutter={[ 8, 8 ]}
            justify="end"
            align="middle"
          >
            <Col>
              <Space>
                <Link to={urlPagePushNotifications()}>
                  <Button
                    type="primary"
                    className="hp-mr-sm-8"
                    ghost="true"
                    icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
                  >
                    <IntlMessages id='ui-general-back-to-list' />
                  </Button>
                </Link>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <FormEditPushNotification
              initialErrors={modifiedErrors}
              initialValues={initValues}
              templateFields={pushNotification?.template?.data?.fields?.data || []}
              templateOptions={[ {
                label: pushNotification?.template?.data?.title,
                value: pushNotification?.template?.data?.id,
              } ]}
              validationSchema={validation}
              isSubmitting={isFetching || isUpdatingPushNotification}
              onCancel={() => {}}
              onSubmit={onSubmit}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageEditPushNotification;
